import React, { createContext, useContext, useState, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Alert, Box } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

const ErrorModalContext = createContext(null);

export const useErrorModal = () => {
  const context = useContext(ErrorModalContext);
  if (!context) {
    throw new Error('useErrorModal must be used within ErrorModalProvider');
  }
  return context;
};

export const ErrorModalProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    open: false,
    errorData: null,
  });

  const showError = useCallback((errorData) => {
    if (!errorData) return;

    // If errorData is a string, convert to ErMsg format
    if (typeof errorData === 'string') {
      errorData = {
        DisplayAs: 'T',
        MsgType: 'E',
        Text: errorData,
      };
    }

    // Handle based on DisplayAs property
    if (errorData.DisplayAs === 'M') {
      // Show as modal
      setModalState({
        open: true,
        errorData,
      });
    } else {
      // Show as toast (default)
      const variant = errorData.MsgType === 'E' ? 'error' : 
                     errorData.MsgType === 'A' ? 'warning' : 
                     errorData.MsgType === 'I' ? 'info' : 'success';
      
      const message = errorData.Header ? 
        `${errorData.Header}: ${errorData.Text}` : 
        errorData.Text;
      
      enqueueSnackbar(message, { variant });
    }
  }, []);

  const closeModal = useCallback(() => {
    setModalState({
      open: false,
      errorData: null,
    });
  }, []);

  const getAlertSeverity = (msgType) => {
    switch (msgType) {
      case 'E': return 'error';
      case 'A': return 'warning';
      case 'I': return 'info';
      case 'R': return 'success';
      default: return 'error';
    }
  };

  return (
    <ErrorModalContext.Provider value={{ showError }}>
      {children}
      <Dialog
        open={modalState.open}
        onClose={closeModal}
        maxWidth="sm"
        fullWidth
        data-testid="error-modal-context--Dialog-0">
        {modalState.errorData && (
          <>
            <DialogTitle data-testid="error-modal-context--DialogTitle-0">
              {modalState.errorData.Header || 'Error'}
            </DialogTitle>
            <DialogContent data-testid="error-modal-context--DialogContent-0">
              <Alert
                severity={getAlertSeverity(modalState.errorData.MsgType)}
                sx={{ mb: 2 }}
                data-testid="error-modal-context--Alert-0">
                <Typography variant="body1">
                  {modalState.errorData.Text}
                </Typography>
              </Alert>
              {modalState.errorData.LogId && !modalState.errorData.HideLogIdYN && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Log ID: {modalState.errorData.LogId}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions data-testid="error-modal-context--DialogActions-0">
              <Button
                onClick={closeModal}
                color="primary"
                data-testid="error-modal-context--Button-0">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </ErrorModalContext.Provider>
  );
};